import { useAppContext } from '@faro/app-context';
import {
    EntityCommandCreateResponse,
    RevisionedCommandResponse,
    StudyEntity,
    StudyEntityType,
} from '@faro/study-designer-model';
import axios from 'axios';
import { omit } from 'lodash';
import { createStudyEntityHookFactory } from './createStudyEntityHookFactory';

/** Inserts a section into a study, resolving with the command's response. */
export type SectionInsertHook<T> = (section: T) => Promise<RevisionedCommandResponse<EntityCommandCreateResponse>>;

/**
 * Creates the mutation hook that inserts a type of section into a study, reporting a failure with the given message.
 *
 * @param entityType the section's entity type, which keys its mutations
 * @param resourcePath the section type's path in the study API, e.g. `folder-section`
 * @param errorMessage shown when the insert fails
 */
export function createSectionInsertMutation<T extends Partial<StudyEntity>>(
    entityType: StudyEntityType,
    resourcePath: string,
    errorMessage: string
): (studyId: string) => SectionInsertHook<T> {
    // The study is kept apart from the section, as it is in the path rather than the body
    const useInsert = createStudyEntityHookFactory(entityType).mutate(
        ({
            studyId,
            section,
        }: {
            studyId: string;
            section: T;
        }): Promise<RevisionedCommandResponse<EntityCommandCreateResponse>> =>
            axios
                .post(`/api/study-designer/studies/${studyId}/${resourcePath}`, omit(section, 'studyId'))
                .then(response => response.data)
    );

    return function useSectionInsertMutation(studyId: string): SectionInsertHook<T> {
        const { showErrorHandler } = useAppContext();
        const insert = useInsert({ studyId });

        return section => insert.mutateAsync({ studyId, section }).catch(showErrorHandler(errorMessage));
    };
}
